import { ImageOff } from 'lucide-react';

import type { ImageContent, ScriptElement } from '@/lib/schema.types';

interface Props {
  element: ScriptElement;
  /**
   * When `true`, the image fills its parent container (used by freeform mode
   * where the wrapper sets the explicit width/height). When `false`, the image
   * sizes itself according to `style.width` / `style.height` if present, or
   * falls back to its intrinsic size constrained by the column width.
   */
  fill?: boolean;
}

export function ImageElement({ element, fill }: Props) {
  const content = element.content as ImageContent | undefined;
  const radius = (element.style?.borderRadius as number | undefined) ?? 0;
  const w = element.style?.width as number | string | undefined;
  const h = element.style?.height as number | string | undefined;
  const crop = content?.crop;

  if (!content?.src) {
    return (
      <div
        style={fill ? { width: '100%', height: '100%' } : undefined}
        className="flex aspect-video w-full items-center justify-center rounded-md bg-black/5 text-fg-subtle"
      >
        <ImageOff size={20} />
      </div>
    );
  }

  const style: React.CSSProperties = fill
    ? {
        width: '100%',
        height: '100%',
        borderRadius: radius,
      }
    : {
        width: typeof w === 'number' ? `${w}px` : w,
        height: typeof h === 'number' ? `${h}px` : h,
        maxWidth: '100%',
        borderRadius: radius,
      };

  // When cropped, clip to the crop region: a wrapper hides the overflow and the
  // image is scaled (1/crop.w × 1/crop.h) + translated so the region fills it.
  if (crop && crop.w > 0 && crop.h > 0) {
    return (
      <div style={{ ...style, overflow: 'hidden', position: 'relative' }}>
        <img
          src={content.src}
          alt={content.alt ?? ''}
          draggable={false}
          className="block max-w-none"
          style={{
            position: 'absolute',
            width: `${100 / crop.w}%`,
            height: `${100 / crop.h}%`,
            left: `${-(crop.x / crop.w) * 100}%`,
            top: `${-(crop.y / crop.h) * 100}%`,
            objectFit: 'cover',
          }}
        />
      </div>
    );
  }

  // eslint-disable-next-line @next/next/no-img-element
  return (
    <img
      src={content.src}
      alt={content.alt ?? ''}
      style={style}
      className="block object-contain"
      draggable={false}
    />
  );
}
